import type { TicketSettings } from '@pulsify/shared/dto'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useRefreshPayoutSetup, useStartPayoutSetup } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { type PayoutReturn, payoutReturnUrls } from './payout-return'

export function PayoutSetupButton({
  businessId,
  hostProfileId,
  started,
  label,
  variant = 'default',
}: {
  businessId: string
  hostProfileId?: string
  started: boolean
  label?: string
  variant?: 'default' | 'outline'
}) {
  const start = useStartPayoutSetup(businessId, hostProfileId)
  const begin = () =>
    start.mutate(payoutReturnUrls(window.location.href), {
      onSuccess: (url) => {
        window.location.href = url
      },
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })

  return (
    <Button
      size="sm"
      variant={variant}
      disabled={start.isPending || start.isSuccess}
      onClick={begin}
    >
      {label ?? (started ? m.ticketsPayoutsContinue() : m.ticketsPayoutsStart())}
    </Button>
  )
}

export function PayoutSetupPrompt({
  businessId,
  hostProfileId,
  settings,
}: {
  businessId: string
  hostProfileId?: string
  settings: TicketSettings
}) {
  const started = settings.connect.accountId !== null
  if (!settings.connect.available) {
    return <p className="rounded-lg bg-muted px-3 py-2.5 text-sm">{m.ticketsPaidSoon()}</p>
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2.5">
      <p className="min-w-0 flex-1 text-sm">
        {started ? m.ticketsPayoutsUnfinished() : m.ticketsPayoutsNeeded()}
      </p>
      <PayoutSetupButton businessId={businessId} hostProfileId={hostProfileId} started={started} />
    </div>
  )
}

export function usePayoutReturn(
  businessId: string,
  back: PayoutReturn | undefined,
  leave: () => void,
  hostProfileId?: string,
) {
  const refresh = useRefreshPayoutSetup(businessId, hostProfileId)
  const handled = useRef(false)
  const refreshMutate = refresh.mutate

  useEffect(() => {
    if (!back || !businessId || handled.current) return
    handled.current = true
    refreshMutate(undefined, {
      onSuccess: (settings) => {
        if (settings.connect.onboardingComplete) toast.success(m.ticketsPayoutsDone())
      },
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })
    leave()
  }, [back, businessId, leave, refreshMutate])
}
